import { useCallback, useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CATALOG_SECTIONS, catalogPath } from '../../config/catalogSections';
import { CatalogRouteContext } from '../../contexts/CatalogRouteContext';
import { useProductFromUrl } from '../../hooks/useProductFromUrl';
import { idFromSlug, slugWithId } from '../../utils/slug';
import Filter from '../../component/filter/Filter';
import SearchInput from '../../component/search/SearchInput';
import TitleSection from '../../component/titleSection/TitleSection';
import ProductSelection from '../../component/product/ProductSelecction';
import Spinner from '../../component/spinner/Spinner';

/**
 * Vista única del catálogo. Reemplaza a RepairView, SpartPartView,
 * KitMaintenanceView, AccessoryView, ManualsView y ProductView, que eran el
 * mismo archivo con el título y el orden del acordeón cambiados.
 *
 * Todo el estado vive en la URL:
 *   /:seccion/:brandSlug?/:productSlug?/:tab?/:solutionParam?/:docParam?
 */
export default function CatalogView({ section }) {
    const { brandSlug, productSlug, tab, solutionParam, docParam } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const config = CATALOG_SECTIONS[section];
    const { product, loading, notFound } = useProductFromUrl(productSlug);

    // Formato viejo de URL (?brand=&product=). Se redirige para no romper links
    // ya compartidos.
    const legacyBrand = searchParams.get('brand');
    const legacyProduct = searchParams.get('product');
    const isLegacyUrl = Boolean(legacyBrand || legacyProduct);

    // Un :tab que no existe en esta sección se ignora y abre el primero.
    const activeTab = config.tabs.includes(tab) ? tab : config.tabs[0];

    const solutionId = idFromSlug(solutionParam);
    const docId = idFromSlug(docParam);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [section, productSlug]);

    const buildPath = useCallback(
        ({ tab: nextTab = activeTab, solution, doc } = {}) => catalogPath(
            section,
            brandSlug,
            productSlug,
            nextTab,
            solution ? slugWithId(solution.id, solution.name) : null,
            doc ? slugWithId(doc.id, doc.name) : null,
        ),
        [section, brandSlug, productSlug, activeTab]
    );

    const routeValue = useMemo(() => ({
        section,
        brandSlug,
        productSlug,
        tab: activeTab,
        solutionId,
        docId,
        buildPath,

        // Cambiar de pestaña o de solución no debería llenar el historial de
        // entradas que el botón Atrás tenga que deshacer una por una; abrir un
        // documento sí, para que Atrás cierre el modal.
        goToTab: (nextTab) => navigate(buildPath({ tab: nextTab }), { replace: true }),
        openSolution: (solution) => navigate(buildPath({ solution }), { replace: true }),
        closeSolution: (parentSolution) => navigate(buildPath({ solution: parentSolution }), { replace: true }),
        openDoc: (solution, doc) => navigate(buildPath({ solution, doc })),
        closeDoc: (solution) => navigate(buildPath({ solution }), { replace: true }),
    }), [section, brandSlug, productSlug, activeTab, solutionId, docId, buildPath, navigate]);

    if (isLegacyUrl) {
        return <Navigate replace to={catalogPath(section, legacyBrand, legacyBrand ? legacyProduct : null)} />;
    }

    return (
        <CatalogRouteContext.Provider value={routeValue}>
            <div className="containerPortal paddingSection">
                <TitleSection title={config.title} />
                <div className="row">
                    <div className="col-sm-12 col-md-3 col-lg-2">
                        <div style={{ paddingBottom: '2rem' }}>
                            <h5>Ingrese o Seleccione su Modelo</h5>
                            <SearchInput section={section} />
                        </div>
                        <Filter section={section} brandSlug={brandSlug} productSlug={productSlug} />
                    </div>
                    <div className="col-sm-12 col-md-9 col-lg-10">
                        <CatalogContent
                            product={product}
                            productSlug={productSlug}
                            loading={loading}
                            notFound={notFound}
                            tabs={config.tabs}
                            activeTab={activeTab}
                            onTabChange={routeValue.goToTab}
                        />
                    </div>
                </div>
            </div>
        </CatalogRouteContext.Provider>
    );
}

function CatalogContent({ product, productSlug, loading, notFound, tabs, activeTab, onTabChange }) {
    const message = (text) => (
        <h3 style={{ textAlign: 'center', margin: '2rem 0rem' }}>{text}</h3>
    );

    if (!productSlug) {
        return message('Seleccione una Marca y un Producto para ver Todo');
    }

    // Antes este caso (slug en la URL sin producto en memoria) no mostraba nada:
    // ni mensaje ni spinner. Era la pantalla en blanco de los deep links.
    if (loading) {
        return <Spinner />;
    }

    if (notFound) {
        return message('No encontramos ese equipo. Probá buscarlo por marca o por modelo.');
    }

    if (!product) {
        return message('No pudimos cargar este equipo. Reintentá en unos minutos.');
    }

    return (
        <ProductSelection
            product={product}
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={onTabChange}
        />
    );
}
